// Comprehensive Wisconsin family law data based on Custody X Change and official Wisconsin sources
const wisconsinData = {
  wisconsin: {
    name: 'Wisconsin',
    overview: {
      keyPoints: [
        'Wisconsin follows the "best interests of the child" standard with 17 specific factors outlined in Wisconsin Statutes § 767.41',
        'Joint legal custody is presumed unless proven contrary to the child\'s best interests',
        'Wisconsin uses "legal custody" and "physical placement" terminology instead of traditional custody/visitation',
        'The state requires detailed parenting plans for all cases involving minor children',
        'Wisconsin has strong emphasis on mediation and collaborative approaches to resolve family disputes',
        'Physical placement schedules must be specific and detailed to avoid future conflicts',
        'Wisconsin law requires both parents to attend family court counseling in contested cases'
      ],
      uniqueFeatures: [
        'Wisconsin uses "physical placement" instead of visitation, emphasizing parental time rather than access',
        'The state has mandatory family court counseling requirements for contested custody cases',
        'Wisconsin allows for informal family court conferences to resolve disputes without formal hearings',
        'Strong preference for shared placement arrangements when appropriate for the child',
        'Wisconsin has specific statutory factors for relocation cases (move-away situations)',
        'The state recognizes grandparent visitation rights under specific circumstances',
        'Wisconsin has specialized family court commissioners in addition to judges'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in Wisconsin for at least 6 months before filing, or Wisconsin must be the child\'s home state under UCCJEA',
      filing: 'File in the county where either parent resides or where the child has lived for the past 6 months',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, court order, or presumption',
      documentation: 'Required documents include summons and petition, financial disclosure statement, and proposed parenting plan',
      uniqueRules: [
        'Both parents must attend family court counseling in contested custody cases',
        'Wisconsin requires financial disclosure statements in all family law cases involving children',
        'Guardian ad litem may be appointed to represent the child\'s interests in contested cases',
        'Mediation is strongly encouraged and may be ordered by the court',
        'Wisconsin allows for informal family court conferences to streamline the process'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Wisconsin uses "physical placement" to describe where and when the child spends time with each parent',
      legalCustody: 'Legal custody involves decision-making authority for major life decisions including education, healthcare, and religious upbringing',
      jointCustody: 'Joint legal custody is presumed and allows both parents to make major decisions unless proven contrary to best interests',
      visitation: 'Called "physical placement" - includes detailed schedules specifying when the child is with each parent',
      specialProvisions: [
        'Supervised physical placement may be ordered when child safety is a concern',
        'Shared physical placement (equal or near-equal time) is encouraged when appropriate',
        'Extended placement during school breaks and summer vacation is common',
        'Holiday and special occasion placement alternates between parents annually',
        'Virtual placement (video calls, phone contact) is recognized for long-distance situations',
        'Grandparent placement rights are protected under Wisconsin Statute § 767.43'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed physical placement schedule including regular rotation, weekends, holidays, and school breaks',
        'Specific holiday and vacation schedules with clear alternating arrangements between parents',
        'Transportation arrangements and responsibility for costs between parents\' residences',
        'Communication methods between parents and guidelines for parent-child contact during placement',
        'Decision-making authority allocation for education, healthcare, religious, and extracurricular activities',
        'Dispute resolution procedures including mediation requirements before court intervention',
        'Relocation provisions and notification requirements for moves affecting placement schedules',
        'Any restrictions, conditions, or special accommodations for either parent',
        'Child care arrangements during work hours and right of first refusal provisions',
        'Guidelines for introducing new romantic partners to the children'
      ],
      tips: [
        'Be extremely specific about pickup and drop-off times, locations, and procedures to avoid confusion',
        'Include detailed provisions for Wisconsin\'s winter weather and how it affects transportation',
        'Address how to handle missed placement due to weather, illness, work, or emergencies',
        'Specify meeting locations for exchanges, especially for parents living far apart',
        'Include provisions for the child\'s participation in extracurricular activities and school events',
        'Address how major decisions will be made and communicated between parents',
        'Include technology use guidelines and virtual placement provisions for maintaining contact',
        'Plan for extended summer placement arrangements to maximize time with both parents'
      ]
    },
    childSupport: {
      guidelines: 'Wisconsin uses a percentage of income model based on the paying parent\'s gross income and number of children',
      factors: [
        'Gross income of the paying parent from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Physical placement time (significant placement can reduce support obligation)',
        'Special needs of the children requiring additional expenses'
      ],
      calculation: 'Wisconsin Child Support Guidelines using percentage-of-income model with standard percentages',
      modifications: 'Substantial change in circumstances required, typically 15% change in income or circumstances affecting support',
      enforcement: 'Strong enforcement through Wisconsin Department of Children and Families including wage garnishment, tax intercept, and license suspension',
      detailedInfo: {
        basicCalculation: {
          description: 'Wisconsin uses a percentage-of-income model where child support is calculated as a percentage of the paying parent\'s gross income based on the number of children.',
          steps: [
            'Calculate the paying parent\'s gross monthly income from all verifiable sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Apply the Wisconsin percentage guidelines based on number of children (17% for 1, 25% for 2, etc.)',
            'Add costs for health insurance premiums specifically for the children',
            'Add necessary childcare expenses for employment or education',
            'Add extraordinary medical expenses exceeding $250 per year per child',
            'Apply any adjustments for significant physical placement time (25%+ overnights)',
            'Calculate final monthly support amount payable by the obligor parent'
          ]
        },
        percentageGuidelines: {
          description: 'Wisconsin uses standard percentages of gross income based on number of children',
          percentages: [
            '1 child: 17% of gross income',
            '2 children: 25% of gross income',
            '3 children: 29% of gross income',
            '4 children: 31% of gross income',
            '5 or more children: 34% of gross income'
          ],
          notes: [
            'Percentages apply to gross income up to $150,000 annually',
            'Court may deviate from guidelines for high-income cases',
            'Shared placement may result in different calculation methods',
            'Multiple family situations require special worksheets'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Military pay including base pay, allowances, and special pay',
            'Spousal support or maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Business income from partnerships, corporations, or LLCs'
          ],
          excluded: [
            'Means-tested public assistance (W-2, FoodShare, etc.)',
            'Foster care payments received for non-biological children',
            'Child support received for other children not subject to current order',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or cohabitating partner',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Reimbursement for job-related expenses'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status',
            'State income taxes (Wisconsin state taxes)',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal support/maintenance to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child (added separately to support)',
            'Childcare expenses necessary for employment or education',
            'Extraordinary medical expenses for the child (shared proportionally)'
          ]
        },
        placementAdjustment: {
          description: 'Wisconsin allows adjustment to child support when the paying parent has significant physical placement time with the child.',
          thresholds: [
            'No adjustment for less than 25% physical placement (92 nights per year)',
            'Potential adjustment for 25-30% physical placement (92-109 nights per year)',
            'Standard shared placement adjustment for 30%+ physical placement (110+ nights per year)',
            'Equal placement (50/50) may result in no support or offset calculations'
          ],
          calculation: 'Adjustment considers additional direct costs during placement and reduced costs to primary parent',
          factors: [
            'Actual overnight placement documented over previous 12 months',
            'Additional housing costs for accommodating child during placement',
            'Duplicate expenses (clothing, toys, school supplies) maintained in both homes',
            'Transportation costs for placement exchanges',
            'Reduced household expenses for primary parent during placement periods'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically attributable to covering the child',
            allocation: 'Added to basic support amount - paying parent responsible for cost',
            requirements: 'Must be reasonable in cost, accessible to child, and provide adequate coverage',
            considerations: [
              'Premium cost must be specifically for child coverage',
              'Insurance must be available through employer or affordable marketplace',
              'Coverage should include medical, dental, and vision if reasonably available',
              'Both parents should have access to insurance information and cards'
            ]
          },
          childcare: {
            description: 'Necessary childcare costs directly related to employment, job training, or education',
            allocation: 'Added to basic support amount - paying parent responsible for reasonable costs',
            requirements: 'Must be work-related or education-related necessity for receiving parent',
            considerations: [
              'Childcare must be necessary for parent to work or attend school',
              'Costs should be reasonable for the local area and type of care needed',
              'Before and after school care is typically included',
              'Summer childcare during school breaks may be included',
              'Documentation and receipts required for verification'
            ]
          },
          extraordinaryMedical: {
            description: 'Uninsured medical, dental, and vision expenses exceeding $250 per year per child',
            examples: [
              'Dental treatment not covered by insurance (fillings, crowns, extractions)',
              'Orthodontic treatment and appliances',
              'Vision care including eye exams, glasses, and contact lenses',
              'Mental health counseling and therapy sessions',
              'Physical therapy and rehabilitation services',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies (braces, wheelchairs, etc.)',
              'Treatment for chronic conditions requiring ongoing care',
              'Emergency medical treatment and hospitalization costs',
              'Specialized medical care and consultations with specialists'
            ],
            allocation: 'Added to basic support amount after $250 annual threshold per child',
            procedure: 'Receiving parent should seek advance approval for non-emergency expenses over $500'
          },
          extracurricular: {
            description: 'Costs for children\'s activities, sports, and enrichment programs',
            examples: [
              'Youth sports team fees, equipment, and travel costs',
              'Music lessons and instrument rental or purchase',
              'Dance, martial arts, gymnastics, or other activity classes',
              'Summer camps and recreational day programs',
              'School trips, field trips, and educational activities',
              'Tutoring and educational support services',
              'Art, theater, or other creative programs'
            ],
            note: 'Not automatically included in basic support - must be agreed upon by parents or court-ordered',
            considerations: [
              'Activities should be in the child\'s best interest and age-appropriate',
              'Costs should be reasonable and within family\'s financial means',
              'Both parents should have input on major extracurricular decisions',
              'Transportation to activities should be considered in planning'
            ]
          }
        },
        modifications: {
          grounds: [
            'Substantial change in either parent\'s income (typically 15% or more)',
            'Significant change in physical placement arrangement',
            'Change in health insurance costs or availability',
            'Change in childcare expenses or needs',
            'Child\'s needs change significantly (special needs, medical conditions)',
            'Emancipation, marriage, or death of a child',
            'Three years have passed since last order (allows for cost-of-living review)',
            'Change in number of children covered by the order'
          ],
          process: [
            'File Motion for Revision of Child Support with supporting documentation',
            'Serve the other parent with motion and updated financial disclosure',
            'Provide current income verification and expense documentation',
            'Attend court hearing or family court conference as scheduled',
            'Present evidence supporting the requested modification',
            'Obtain revised court order with new support amount and effective date'
          ],
          timeline: 'Modifications are typically effective from the date of filing the motion, not the date of the final order',
          documentation: [
            'Updated Financial Disclosure Statement with current income and expenses',
            'Recent pay stubs, tax returns, and employment verification',
            'Documentation of changed circumstances (medical bills, job loss, etc.)',
            'Current childcare and health insurance costs',
            'Evidence of physical placement changes if applicable'
          ]
        },
        enforcement: {
          methods: [
            'Income withholding and automatic wage garnishment',
            'Interception of tax refunds (federal and state)',
            'Asset seizure including bank accounts and personal property',
            'Liens on real estate and personal property',
            'Suspension of driver\'s license and professional licenses',
            'Suspension of hunting, fishing, and recreational licenses',
            'Credit bureau reporting of delinquent support',
            'Contempt of court proceedings with possible jail time',
            'Passport denial for arrearages exceeding $2,500',
            'Interception of lottery winnings and gaming proceeds',
            'Garnishment of unemployment and workers\' compensation benefits'
          ],
          interest: 'Unpaid child support accrues interest at the rate of 1% per month (12% annually) on past-due amounts',
          statute: 'No statute of limitations exists for collecting past-due child support - it remains collectible indefinitely',
          resources: [
            'Wisconsin Department of Children and Families Child Support Program',
            'Local child support agencies in each county',
            'Private collection agencies contracted by the state',
            'Interstate enforcement through other state agencies'
          ]
        },
        calculator: {
          worksheets: [
            'Wisconsin Child Support Obligation Worksheet (basic calculation)',
            'Shared Physical Placement Worksheet (for significant placement time)',
            'Multiple Family Worksheet (when parent has children with multiple partners)',
            'High Income Worksheet (for income exceeding guideline amounts)'
          ],
          online: 'Wisconsin Courts provide online calculator tools for estimation purposes',
          accuracy: 'Court calculations may differ from estimates based on specific case circumstances and judicial discretion',
          updates: 'Guidelines are reviewed periodically and updated as necessary by the Wisconsin Legislature'
        }
      }
    },
    resources: {
      courts: [
        {
          name: 'Wisconsin Court System Family Court Resources',
          url: 'https://www.wicourts.gov/services/public/selfhelp/family.htm',
          description: 'Official Wisconsin court system family division with forms, procedures, and self-help resources'
        },
        {
          name: 'Wisconsin Family Court Forms',
          url: 'https://www.wicourts.gov/forms1/circuit/ccform.jsp?Category=14',
          description: 'Complete collection of Wisconsin family court forms for divorce, custody, and support cases'
        },
        {
          name: 'Wisconsin Self-Help Law Center',
          url: 'https://www.wicourts.gov/services/public/selfhelp/',
          description: 'Comprehensive self-help resources for people representing themselves in family court'
        },
        {
          name: 'Milwaukee County Family Court',
          address: '901 North 9th Street, Milwaukee, WI 53233',
          phone: '(414) 278-4120',
          description: 'Family court serving Milwaukee County including Milwaukee metropolitan area'
        },
        {
          name: 'Dane County Family Court',
          address: '215 South Hamilton Street, Madison, WI 53703',
          phone: '(608) 266-4311',
          description: 'Family court serving Dane County including Madison area'
        },
        {
          name: 'Brown County Family Court',
          address: '300 East Walnut Street, Green Bay, WI 54301',
          phone: '(920) 448-4281',
          description: 'Family court serving Brown County including Green Bay area'
        }
      ],
      support: [
        {
          name: 'Wisconsin Department of Children and Families - Child Support',
          url: 'https://dcf.wisconsin.gov/cs',
          phone: '(414) 227-4300',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Wisconsin Child Support Payment Center',
          url: 'https://dcf.wisconsin.gov/cs/pay',
          description: 'Official payment processing and case management for Wisconsin child support'
        },
        {
          name: 'Legal Action of Wisconsin',
          url: 'https://www.legalaction.org/',
          phone: '(414) 278-7722',
          description: 'Free legal services for low-income families in family law matters statewide'
        },
        {
          name: 'State Bar of Wisconsin Lawyer Referral Service',
          url: 'https://www.wisbar.org/forPublic/INeedaLawyer/Pages/Lawyer-Referral-Service.aspx',
          phone: '(800) 362-9082',
          description: 'Professional attorney referrals for family law cases throughout Wisconsin'
        },
        {
          name: 'Wisconsin Coalition Against Domestic Violence',
          url: 'https://www.endabusewi.org/',
          phone: '(608) 255-0539',
          description: 'Statewide domestic violence resources, shelters, and legal advocacy services'
        },
        {
          name: 'Wisconsin Fathers for Equal Rights',
          url: 'https://www.facebook.com/groups/wisconsinfathersrights',
          description: 'Support networks and advocacy groups for fathers navigating family court in Wisconsin'
        }
      ],
      legal: [
        {
          name: 'Wisconsin State Bar Association Family Law Section',
          url: 'https://www.wisbar.org/aboutUs/sections/familylaw/Pages/default.aspx',
          description: 'Professional resources and directory of family law attorneys in Wisconsin'
        },
        {
          name: 'University of Wisconsin Law School Legal Clinic',
          url: 'https://law.wisc.edu/academics/clinics/',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Marquette University Law School Legal Clinic',
          url: 'https://law.marquette.edu/law-clinics',
          description: 'Clinical legal services including family law representation'
        },
        {
          name: 'Wisconsin Collaborative Law Institute',
          url: 'https://www.collaborativelaw.org/find-a-professional/wisconsin/',
          description: 'Network of collaborative divorce attorneys and professionals in Wisconsin'
        },
        {
          name: 'Wisconsin Mediation Association',
          url: 'https://www.wisconsinmediation.org/',
          description: 'Family mediation services and dispute resolution professionals statewide'
        }
      ],
      educational: [
        {
          name: 'Children First Program',
          url: 'https://www.wicourts.gov/services/public/selfhelp/family/children.htm',
          description: 'Parent education program for divorcing parents with minor children (required in many counties)'
        },
        {
          name: 'Wisconsin Parenting Plan Resources',
          url: 'https://www.wicourts.gov/services/public/selfhelp/family/parenting.htm',
          description: 'Comprehensive guide to creating effective parenting plans in Wisconsin'
        },
        {
          name: 'Wisconsin Guardian ad Litem Program',
          url: 'https://www.wicourts.gov/services/public/gal/',
          description: 'Information about guardian ad litem services for children in contested custody cases'
        },
        {
          name: 'University of Wisconsin Extension - Family Programs',
          url: 'https://fyi.extension.wisc.edu/relationships/',
          description: 'Educational programs for co-parenting, family relationships, and child development'
        },
        {
          name: 'Wisconsin Parent Information and Resource Centers',
          url: 'https://dpi.wi.gov/sped/families/parent-information-centers',
          description: 'Educational resources for parents navigating special needs and educational decisions'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'Milwaukee County Circuit Court',
            address: '901 North 9th Street, Milwaukee, WI 53233',
            phone: '(414) 278-4120',
            jurisdiction: 'Milwaukee County - Milwaukee, West Allis, Wauwatosa, and surrounding communities'
          },
          {
            name: 'Dane County Circuit Court',
            address: '215 South Hamilton Street, Madison, WI 53703',
            phone: '(608) 266-4311',
            jurisdiction: 'Dane County - Madison, Middleton, Sun Prairie, and surrounding areas'
          },
          {
            name: 'Brown County Circuit Court',
            address: '300 East Walnut Street, Green Bay, WI 54301',
            phone: '(920) 448-4281',
            jurisdiction: 'Brown County - Green Bay, De Pere, and surrounding areas'
          },
          {
            name: 'Waukesha County Circuit Court',
            address: '515 West Moreland Boulevard, Waukesha, WI 53188',
            phone: '(262) 548-7209',
            jurisdiction: 'Waukesha County - Waukesha, Brookfield, New Berlin, and surrounding areas'
          },
          {
            name: 'Racine County Circuit Court',
            address: '730 Wisconsin Avenue, Racine, WI 53403',
            phone: '(262) 636-3133',
            jurisdiction: 'Racine County - Racine, Mount Pleasant, and surrounding areas'
          },
          {
            name: 'Kenosha County Circuit Court',
            address: '912 56th Street, Kenosha, WI 53140',
            phone: '(262) 653-2664',
            jurisdiction: 'Kenosha County - Kenosha, Pleasant Prairie, and surrounding areas'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$184.50',
          'Divorce (without children)': '$184.50',
          'Legal Separation': '$184.50',
          'Paternity': '$75.00',
          'Custody/Placement': '$75.00',
          'Child Support': '$75.00',
          'Modification': '$75.00',
          'Restraining Order': 'No fee'
        },
        requiredForms: [
          'Summons and Petition for Divorce or Legal Separation',
          'Financial Disclosure Statement',
          'Parenting Plan (if children involved)',
          'Child Support Obligation Worksheet',
          'Confidential Petition Addendum',
          'Certificate of Service or Admission of Service'
        ]
      },
      statutes: [
        {
          name: 'Wisconsin Statutes Chapter 767 - Actions Affecting the Family',
          url: 'https://docs.legis.wisconsin.gov/statutes/statutes/767',
          description: 'Primary statutes governing divorce, legal separation, and family law in Wisconsin'
        },
        {
          name: 'Wisconsin Child Support Guidelines (Wis. Stat. § 767.511)',
          url: 'https://docs.legis.wisconsin.gov/statutes/statutes/767/V/511',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'Wisconsin Best Interests Factors (Wis. Stat. § 767.41)',
          url: 'https://docs.legis.wisconsin.gov/statutes/statutes/767/IV/41',
          description: 'Seventeen factors courts consider in custody and placement decisions'
        },
        {
          name: 'Wisconsin Uniform Child Custody Jurisdiction Act (Wis. Stat. § 822)',
          url: 'https://docs.legis.wisconsin.gov/statutes/statutes/822',
          description: 'Interstate custody jurisdiction and enforcement laws'
        }
      ],
      emergencyContacts: [
        {
          name: 'Wisconsin Domestic Violence Hotline',
          phone: '1-800-799-7233',
          description: '24/7 statewide domestic violence crisis line and support services'
        },
        {
          name: 'Wisconsin Child Abuse and Neglect Hotline',
          phone: '1-800-252-5437',
          description: '24/7 child abuse and neglect reporting hotline'
        },
        {
          name: 'Wisconsin Crisis Line',
          phone: '211',
          description: 'Dial 211 for mental health crisis intervention and resource referrals'
        },
        {
          name: 'Legal Action of Wisconsin Emergency Line',
          phone: '(414) 278-7722',
          description: 'Emergency legal assistance for urgent family law matters'
        }
      ]
    },
    bestInterestsFactors: [
      'The wishes of the child\'s parent or parents, as shown by any stipulation between the parties, any proposed parenting plan, or otherwise',
      'The wishes of the child, which may be communicated by the child or through the child\'s guardian ad litem or other appropriate professional',
      'The interaction and interrelationship of the child with his or her parent or parents, siblings, and any other person who may significantly affect the child\'s best interest',
      'The amount of time each parent has spent with the child in the past, any necessary changes to the parents\' custodial roles, and the likely effect of such changes on the child',
      'The child\'s adjustment to the home, school, religion, and community',
      'The age of the child and the child\'s developmental and educational needs at different ages',
      'Whether either party has or had a significant problem with alcohol or drug abuse',
      'The mental and physical health of the parties, the minor children, and other persons living in a proposed custodial household',
      'The need for regularly occurring and meaningful periods of physical placement to provide predictability and stability for the child',
      'The availability of public or private child care services',
      'The cooperation and communication between the parties and whether either party unreasonably refuses to cooperate or communicate with the other party',
      'Whether each party can support the other party\'s relationship with the child, including encouraging and facilitating frequent and continuing contact with the child, or whether one party is likely to interfere with the child\'s continuing relationship with the other party',
      'Whether there is evidence that a party engaged in abuse, as defined in s. 813.122(1)(a), of the child',
      'Whether there is evidence of interspousal battery or domestic abuse as defined in s. 813.12(1)(am)',
      'Whether either party has or had any criminal charges or convictions that are substantially related to the welfare of the child',
      'The physical, mental, and emotional needs of the child, including any special needs',
      'Such other factors as the court may in each individual case determine to be relevant'
    ],
    practicalTips: [
      'Wisconsin courts strongly favor detailed, specific parenting plans that minimize future disputes',
      'Document all interactions and communications with the other parent using email or text when possible',
      'Attend all required family court counseling sessions - failure to participate can harm your case',
      'Be prepared for mediation - Wisconsin courts strongly encourage resolution outside of trial',
      'Keep detailed records of your physical placement time and any deviations from the schedule',
      'Follow all court orders precisely - violations can seriously impact future custody decisions',
      'Consider hiring a local attorney familiar with Wisconsin family court procedures and judges',
      'Focus on the child\'s best interests rather than past grievances with the other parent'
    ],
    processingInformation: {
      mediation: {
        description: 'Wisconsin strongly encourages mediation to resolve family law disputes before trial',
        benefits: [
          'Less expensive than going to trial',
          'Faster resolution of disputes',
          'More control over the outcome',
          'Less adversarial than court proceedings',
          'Confidential process that protects privacy',
          'Can preserve co-parenting relationships'
        ],
        process: [
          'Either party can request mediation or court may order it',
          'Both parties must agree to participate in good faith',
          'Mediator helps facilitate discussion and negotiation',
          'Any agreements reached are put in writing',
          'Agreements can be submitted to court for approval',
          'If mediation fails, case proceeds to trial'
        ],
        cost: 'Typically ranges from $100-300 per hour, split between parties'
      },
      familyCourtCounseling: {
        description: 'Wisconsin requires both parents to attend family court counseling in contested custody cases',
        purpose: [
          'Help parents understand the impact of divorce on children',
          'Provide information about child development and co-parenting',
          'Discuss alternatives to litigation',
          'Encourage settlement and cooperation',
          'Identify issues that may need professional intervention'
        ],
        requirements: [
          'Both parents must attend within 60 days of filing',
          'Failure to attend can result in case delays or sanctions',
          'Sessions typically last 2-4 hours',
          'Cost is usually $50-100 per person',
          'Some counties offer sliding scale fees'
        ]
      },
      settlement: {
        description: 'Wisconsin encourages settlement through various alternative dispute resolution methods',
        options: [
          'Mediation with trained family mediators',
          'Collaborative law with specially trained attorneys',
          'Informal family court conferences with commissioners',
          'Settlement conferences with judges',
          'Attorney-to-attorney negotiations'
        ],
        advantages: [
          'Faster resolution than trial',
          'Lower cost than litigation',
          'More privacy and confidentiality',
          'Greater control over outcomes',
          'Less stress and trauma for children',
          'Preserves co-parenting relationships'
        ]
      },
      trial: {
        description: 'If settlement cannot be reached, Wisconsin family court cases proceed to trial',
        process: [
          'Pre-trial conferences to narrow issues',
          'Discovery process to gather evidence',
          'Guardian ad litem investigation if appointed',
          'Trial before family court commissioner or judge',
          'Presentation of evidence and testimony',
          'Final orders and judgment entered'
        ],
        timeline: 'Trials typically occur 6-12 months after filing, depending on court calendar',
        preparation: [
          'Gather all relevant financial documents',
          'Prepare witness lists and evidence',
          'Complete court-ordered evaluations or studies',
          'Attend all required counseling and mediation',
          'Work with attorney to develop case strategy'
        ]
      }
    }
  }
};

export default wisconsinData;